import {
  KitCoachQuoteMetadata,
  QuickQuoteResponseQuote,
  QuoteMetadata,
} from 'lib/api/getQuickQuote';
import { ProfitType } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';

import { MarginBasis, SourceRow, Suggestion } from '../types';

const isCoachMetadata = (value: unknown): value is KitCoachQuoteMetadata => {
  return typeof value === 'object' && value !== null;
};

// Maps a QQ v2 kitcoach quote entry into the InsightsCard suggestion contract.
export function mapSuggestionFromQuote(
  quote: MaybeUndef<QuickQuoteResponseQuote>
): Maybe<Suggestion> {
  if (!quote || quote.source !== Quoting.KitCoach) {
    return null;
  }

  const metadata = quote.metadata as QuoteMetadata & {
    kitCoach?: unknown;
  };
  const payload: Maybe<KitCoachQuoteMetadata> = isCoachMetadata(
    metadata?.kitCoach
  )
    ? metadata.kitCoach
    : null;

  if (!payload) {
    return null;
  }

  const targetBuy = payload.targetBuy ?? quote.rates?.target;
  const buyLow = payload.buyLow ?? quote.rates?.low;
  const buyHigh = payload.buyHigh ?? quote.rates?.high;

  if (
    typeof targetBuy !== 'number' ||
    typeof buyLow !== 'number' ||
    typeof buyHigh !== 'number'
  ) {
    return null;
  }

  const sources: SourceRow[] = Array.isArray(payload.sources)
    ? payload.sources.map((source) => {
        const row: SourceRow = {
          label: source.label,
          buy: source.buy,
          low: source.low,
          high: source.high,
        };
        if (typeof source.confidenceLevel === 'number') {
          row.confidenceLevel = source.confidenceLevel;
        }
        if (source.isLowConfidence === true) {
          row.isLowConfidence = true;
        }
        return row;
      })
    : [];

  const calculationSteps = Array.isArray(payload.calculationSteps)
    ? payload.calculationSteps
        .filter(
          (step): step is { id: string; title: string; description: string } =>
            typeof step?.id === 'string' &&
            typeof step?.title === 'string' &&
            typeof step?.description === 'string'
        )
        .map((step) => ({
          id: step.id,
          title: step.title,
          description: step.description,
        }))
    : undefined;

  return {
    transportType: payload.transportType ?? '',
    targetBuy,
    buyLow,
    buyHigh,
    suggestedMargin: payload.suggestedMargin ?? 10,
    suggestedMarginType:
      payload.suggestedMarginType === ProfitType.Amount
        ? ProfitType.Amount
        : ProfitType.Percentage,
    marginBasis: toMarginBasis(payload.marginBasis),
    distanceMiles: payload.distanceMiles ?? quote.distance ?? 0,
    spreadPercent: payload.spreadPercent ?? 0,
    sources,
    summary:
      typeof payload.summary === 'string' && payload.summary.trim().length > 0
        ? payload.summary.trim()
        : undefined,
    calculationSteps,
    isLowConfidence: payload.isLowConfidence === true,
  };
}

export function findCoachQuote(
  quotes: Undef<QuickQuoteResponseQuote[]>
): Maybe<QuickQuoteResponseQuote> {
  if (!quotes?.length) {
    return null;
  }
  return quotes.find((quote) => quote.source === Quoting.KitCoach) ?? null;
}

const MARGIN_BASIS_VALUES = new Set<string>(Object.values(MarginBasis));

function toMarginBasis(value: Undef<string>): Undef<MarginBasis> {
  return value && MARGIN_BASIS_VALUES.has(value)
    ? (value as MarginBasis)
    : undefined;
}
